import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  approveMbcbReport,
  getMbcbReportDetail,
  requestMbcbRevision,
} from '../api/plantersService';
import { useAuth } from '../contexts/AuthContext';
import {
  canEditMbcbReport,
  canManageMbcbReportForPlanter,
  canShowMbcbReviewActions,
  formatMbcbReportPeriod,
  mbcbReportAvgSundayAttendance,
  mbcbReportStatusLabel,
} from '../utils/mbcbReports';

type SundayKey = 's1' | 's2' | 's3' | 's4' | 's5';

const SUNDAYS: SundayKey[] = ['s1', 's2', 's3', 's4', 's5'];

const fmt = (v: unknown) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
};

const fmtNum = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? String(n) : '—';
};

const fmtMoney = (v: unknown) => {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const narrative = (v: unknown) => {
  if (v == null || v === '') return '—';
  return String(v);
};

const WEEKLY_ROWS: { key: string; label: string }[] = [
  { key: 'worshipAttendance', label: 'Worship attendance' },
];

function parseSundayRow(
  weekly: Record<string, unknown> | null,
  rowKey: string,
): Record<SundayKey, number> {
  const empty: Record<SundayKey, number> = { s1: 0, s2: 0, s3: 0, s4: 0, s5: 0 };
  if (!weekly) return empty;
  const row = weekly[rowKey];
  if (!row || typeof row !== 'object') return empty;
  const o = row as Record<string, unknown>;
  const out = { ...empty };
  SUNDAYS.forEach((sk) => {
    const n = o[sk];
    out[sk] = typeof n === 'number' && !Number.isNaN(n) ? n : 0;
  });
  return out;
}

const PlanterMbcbReportDetail: React.FC = () => {
  const { id, reportId } = useParams<{ id: string; reportId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const planterId = parseInt(id || '0', 10);
  const rid = parseInt(reportId || '0', 10);
  const roleNames = (user?.roles ?? []).map((role) => (typeof role === 'string' ? role : role.name));
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  const { data: report, isLoading, error } = useQuery({
    queryKey: ['mbcbReport', rid],
    queryFn: () => getMbcbReportDetail(rid),
    enabled: !!rid,
  });

  const reportData = report as unknown as Record<string, unknown> | undefined;

  const weekly = useMemo(() => {
    if (!reportData) return null;
    const w = reportData.weeklyMetrics;
    if (!w || typeof w !== 'object') return null;
    return w as Record<string, unknown>;
  }, [reportData]);

  const giving = useMemo(() => {
    if (!reportData) return null;
    const g = reportData.givingBreakdown;
    if (!g || typeof g !== 'object') return null;
    return g as Record<string, unknown>;
  }, [reportData]);

  const worshipAverage = useMemo(() => {
    if (!reportData) return null;
    return (
      Math.round(
        mbcbReportAvgSundayAttendance({
          weeklyMetrics: reportData.weeklyMetrics,
          sundayAttendance:
            typeof reportData.sundayAttendance === 'number' ? reportData.sundayAttendance : null,
          reportMonth: typeof reportData.reportMonth === 'number' ? reportData.reportMonth : null,
          reportYear: typeof reportData.reportYear === 'number' ? reportData.reportYear : null,
        }) * 10,
      ) / 10
    );
  }, [reportData]);

  const income = giving?.income && typeof giving.income === 'object' ? (giving.income as Record<string, unknown>) : null;
  const mission =
    giving?.missionGiving && typeof giving.missionGiving === 'object'
      ? (giving.missionGiving as Record<string, unknown>)
      : null;
  const canManageReport = canManageMbcbReportForPlanter(
    user?.id,
    report?.planter?.assignedHubLeader,
    roleNames,
  );
  const canEdit = canEditMbcbReport(typeof report?.status === 'string' ? report.status : null, canManageReport);
  const canReview = canManageReport && canShowMbcbReviewActions(report?.status ?? null);

  const approveMutation = useMutation({
    mutationFn: () => approveMbcbReport(rid, reviewNotes.trim() || undefined),
    onSuccess: (updated) => {
      queryClient.setQueryData(['mbcbReport', rid], updated);
      queryClient.invalidateQueries({ queryKey: ['planterMbcbReports', planterId] });
      setShowApproveModal(false);
      setReviewNotes('');
    },
  });

  const revisionMutation = useMutation({
    mutationFn: () => requestMbcbRevision(rid, reviewNotes.trim()),
    onSuccess: (updated) => {
      queryClient.setQueryData(['mbcbReport', rid], updated);
      queryClient.invalidateQueries({ queryKey: ['planterMbcbReports', planterId] });
      setShowRevisionModal(false);
      setReviewNotes('');
    },
  });

  const reportPeriodLabel = formatMbcbReportPeriod(
    typeof report?.reportMonth === 'number' ? report.reportMonth : null,
    typeof report?.reportYear === 'number' ? report.reportYear : null,
  );

  if (!rid) {
    return (
      <Layout>
        <div className="p-6">Invalid report.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-0 max-w-4xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(`/planters/${planterId}`)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
          >
            ← Back to planter
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {canReview && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setReviewNotes('');
                    setShowApproveModal(true);
                  }}
                  className="inline-flex items-center rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100"
                >
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setReviewNotes('');
                    setShowRevisionModal(true);
                  }}
                  className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
                >
                  Request revision
                </button>
              </>
            )}
            {canEdit && (
              <Link
                to={`/planters/${planterId}/reports/${rid}/edit`}
                className="inline-flex items-center rounded-md border border-primary-200 bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-100"
              >
                Edit {reportPeriodLabel} report
              </Link>
            )}
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{reportPeriodLabel} report</h1>
        <p className="text-sm text-gray-600 mt-1">
          Story and prayer first—then a light snapshot for {reportPeriodLabel}.
        </p>
        {isLoading && <p className="mt-4 text-sm text-gray-500">Loading…</p>}
        {error && (
          <p className="mt-4 text-sm text-red-600">
            You may not have access to this report, or it no longer exists.
          </p>
        )}
        {report && typeof report === 'object' && (
          <div className="mt-6 bg-white shadow rounded-lg p-6 space-y-8 text-sm">
            <div className="flex flex-wrap gap-4 text-sm border-b border-gray-100 pb-4">
              <div>
                <span className="text-gray-500">Reporting month </span>
                <span className="font-medium text-gray-900">
                  {reportPeriodLabel}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Status </span>
                <span className="font-medium text-gray-900">
                  {mbcbReportStatusLabel(typeof reportData?.status === 'string' ? reportData.status : null)}
                </span>
              </div>
            </div>

            {canManageReport &&
              typeof reportData?.staffInternalNotes === 'string' &&
              reportData.staffInternalNotes.trim() !== '' && (
                <section className="rounded-lg border border-violet-200 bg-violet-50/60 p-4 space-y-2">
                  <h2 className="text-sm font-semibold text-violet-900">Internal notes (staff)</h2>
                  <p className="text-sm text-violet-950 whitespace-pre-wrap">
                    {String(reportData.staffInternalNotes)}
                  </p>
                </section>
              )}

            <section className="rounded-lg border border-amber-100 bg-amber-50/50 p-4 space-y-4">
              <h2 className="font-semibold text-gray-900">Their month in ministry</h2>
              <div>
                <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                  Celebrate
                </p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">
                  {narrative(reportData?.ministryHighlights)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                  Pray with them about
                </p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">
                  {narrative(reportData?.prayerRequests)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                  How the network can help
                </p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">
                  {narrative(reportData?.supportNeeded)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-amber-900/80 uppercase tracking-wide">
                  Anything else
                </p>
                <p className="mt-1 whitespace-pre-wrap text-gray-900">
                  {narrative(reportData?.additionalNotes)}
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-semibold text-gray-900 mb-3">At a glance</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">Worship attendance (weekly average)</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">{fmtNum(worshipAverage)}</dd>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">New members</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">
                    {fmtNum(reportData?.newMembers)}
                  </dd>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">Baptisms</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">
                    {fmtNum(reportData?.baptisms)}
                  </dd>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">New faith decisions (professions)</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">
                    {fmtNum(reportData?.salvations)}
                  </dd>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">Total budget receipts</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">{fmtMoney(reportData?.monthlyIncome)}</dd>
                </div>
                <div className="rounded-md bg-gray-50 p-3">
                  <dt className="text-gray-500 text-xs">Total mission giving</dt>
                  <dd className="font-medium text-gray-900 mt-0.5">{fmtMoney(reportData?.missionsGiving)}</dd>
                </div>
              </dl>
            </section>

            {(income || mission) && (
              <section>
                <h2 className="font-semibold text-gray-900 mb-3">Giving detail</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {income && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Budget receipts</h3>
                      <dl className="space-y-1.5">
                        {(
                          [
                            ['Congregational offering', income.congregationalOffering],
                            ['Sponsoring church(es)', income.sponsoringChurches],
                            ['Association(s)', income.associations],
                            ['MBCB support', income.mbcb],
                            ['Other support', income.otherSupport],
                            ['Total', income.total],
                          ] as [string, unknown][]
                        ).map(([label, val]) => (
                          <div key={label} className="flex justify-between gap-2">
                            <dt className="text-gray-600">{label}</dt>
                            <dd className="font-medium text-gray-900 tabular-nums">{fmtMoney(val)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {mission && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Mission giving</h3>
                      <dl className="space-y-1.5">
                        {(
                          [
                            ['Cooperative Program', mission.cooperativeProgram],
                            ['Association(s)', mission.associations],
                            ['Margaret Lackey', mission.margaretLackey],
                            ['Other mission causes', mission.otherMissionCauses],
                            ['Total', mission.total],
                          ] as [string, unknown][]
                        ).map(([label, val]) => (
                          <div key={label} className="flex justify-between gap-2">
                            <dt className="text-gray-600">{label}</dt>
                            <dd className="font-medium text-gray-900 tabular-nums">{fmtMoney(val)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              </section>
            )}

            {weekly && (
              <section>
                <h2 className="font-semibold text-gray-900 mb-2">Worship by Sunday</h2>
                <p className="text-xs text-gray-500 mb-3">
                  Sunday 1–5 are the Sundays of the reported month.
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left font-semibold text-gray-700 p-2 pr-4">Metric</th>
                        {SUNDAYS.map((sk, idx) => (
                          <th key={sk} className="text-right font-semibold text-gray-700 p-2 w-14">
                            Sun {idx + 1}
                          </th>
                        ))}
                        <th className="text-right font-semibold text-gray-700 p-2 w-16">Avg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {WEEKLY_ROWS.map(({ key, label }) => {
                        const row = parseSundayRow(weekly, key);
                        const positives = SUNDAYS.map((sk) => row[sk]).filter((n) => n > 0);
                        const avg =
                          positives.length > 0
                            ? Math.round((positives.reduce((a, b) => a + b, 0) / positives.length) * 10) / 10
                            : 0;
                        return (
                          <tr key={key} className="border-b border-gray-100 last:border-0">
                            <td className="p-2 pr-4 text-gray-800">{label}</td>
                            {SUNDAYS.map((sk) => (
                              <td key={sk} className="p-2 text-right tabular-nums text-gray-900">
                                {row[sk]}
                              </td>
                            ))}
                            <td className="p-2 text-right font-medium tabular-nums text-gray-900">{avg}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
              Source: {fmt(reportData?.source)}
              {reportData?.externalId
                ? ` · External ID: ${fmt(reportData.externalId)}`
                : ''}
            </p>
          </div>
        )}

        {showApproveModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
              <button
                type="button"
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                aria-label="Close"
                onClick={() => setShowApproveModal(false)}
              />
              <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <h3 className="text-lg font-medium text-gray-900">Approve report</h3>
                <p className="mt-2 text-sm text-gray-500">Optional notes are stored with the approval.</p>
                <label className="mt-4 block text-sm font-medium text-gray-700">Notes (optional)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-sm"
                  placeholder="Add any notes or feedback…"
                />
                <div className="mt-6 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowApproveModal(false)}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate()}
                    className="rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    {approveMutation.isPending ? 'Approving…' : 'Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRevisionModal && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
              <button
                type="button"
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                aria-label="Close"
                onClick={() => setShowRevisionModal(false)}
              />
              <div className="relative z-10 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
                <h3 className="text-lg font-medium text-gray-900">Request revision</h3>
                <p className="mt-2 text-sm text-gray-500">Explain what the planter should update.</p>
                <label className="mt-4 block text-sm font-medium text-gray-700">Revision notes (required)</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-sm"
                  placeholder="What should they change or add?"
                />
                <div className="mt-6 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRevisionModal(false)}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={revisionMutation.isPending || !reviewNotes.trim()}
                    onClick={() => revisionMutation.mutate()}
                    className="rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    {revisionMutation.isPending ? 'Sending…' : 'Request revision'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlanterMbcbReportDetail;
